import { Component } from '@theme/component';

const CARD_SECTION_ID = 'dev-shop-look-card-render';
const FETCH_CONCURRENCY = 4;

/** @type {Map<string, HTMLElement>} dialog id → last early click before CE ready */
const pendingTriggerByDialogId = new Map();

/**
 * Capture clicks before <dev-shop-look-drawer> upgrades (module load / CE connect gap).
 * Instance listeners take over once `data-shop-look-ready` is set.
 */
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-shop-look-trigger]');
    if (!trigger) return;

    const controls = trigger.getAttribute('aria-controls');
    if (!controls) return;

    const dialog = document.getElementById(controls);
    const host = dialog?.closest('dev-shop-look-drawer');
    if (host?.hasAttribute('data-shop-look-ready')) return;

    event.preventDefault();
    pendingTriggerByDialogId.set(controls, /** @type {HTMLElement} */ (trigger));
  },
  true
);

/**
 * @typedef {{ handle: string, url: string }} ShopLookProductRef
 * @typedef {{
 *   panelId: string,
 *   labels: { tabs: string, inPhoto: string, similar: string, empty: string },
 *   inPhoto: ShopLookProductRef[],
 *   similar: ShopLookProductRef[],
 * }} ShopLookPanelData
 * @typedef {{
 *   inPhotoHtml: string[],
 *   similarHtml: string[],
 *   inPhotoLoaded: boolean,
 *   similarLoaded: boolean,
 * }} ShopLookLookCache
 */

/**
 * Shared shop-the-look drawer — SSR <template> clone, or lazy Section Rendering from JSON handles.
 * Triggers may live outside this element; matched via aria-controls → dialog id.
 *
 * Visual compact title: only the active tab label (or single-list heading) is mirrored
 * into the titlebar via shared `dev-drawer.js` (`data-dev-drawer-heading`).
 *
 * @extends {Component}
 */
class DevShopLookDrawer extends Component {
  /** @type {Map<string, ShopLookLookCache>} */
  #lookCache = new Map();

  /** @type {Map<string, Promise<void>>} lookId:tab → in-flight load (dedupe prefetch∥open) */
  #inflightLoads = new Map();

  /** @type {AbortController | null} */
  #openAbort = null;

  /** @type {string | null} */
  #activeLookId = null;

  /** @type {ShopLookPanelData | null} */
  #activePanelData = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener('pointerenter', this.#handlePointerEnter, true);
    document.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('keydown', this.#handleTabKeydown);
    this.addEventListener('click', this.#handleDrawerClick);
    this.setAttribute('data-shop-look-ready', '');
    this.#flushPendingOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeAttribute('data-shop-look-ready');
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('pointerenter', this.#handlePointerEnter, true);
    document.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('keydown', this.#handleTabKeydown);
    this.removeEventListener('click', this.#handleDrawerClick);
    this.#openAbort?.abort();
  }

  /** Open look queued by early capture listener (pre-ready clicks). */
  #flushPendingOpen() {
    const dialogEl = this.#getDialogEl();
    const dialogId = dialogEl?.id;
    if (!dialogId) return;

    const trigger = pendingTriggerByDialogId.get(dialogId);
    if (!trigger) return;

    pendingTriggerByDialogId.delete(dialogId);
    this.#openShopDrawer(trigger);
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getDialogEl() {
    return this.querySelector('[data-shop-look-dialog]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getDrawerRoot() {
    return this.querySelector('[data-shop-look-drawer-root]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getDrawerBody() {
    return this.querySelector('[data-shop-look-drawer-body]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getDrawerContent() {
    return this.querySelector('[data-shop-look-content]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getDrawerStatus() {
    return this.querySelector('[data-shop-look-status]');
  }

  /**
   * @returns {import('./dialog').DialogComponent | null}
   */
  #getDialogComponent() {
    return /** @type {import('./dialog').DialogComponent | null} */ (this.querySelector('dialog-component'));
  }

  /**
   * @param {Element} trigger
   * @returns {boolean}
   */
  #triggerMatchesThisDrawer(trigger) {
    const dialogEl = this.#getDialogEl();
    const controls = trigger.getAttribute('aria-controls');
    return Boolean(dialogEl && controls && dialogEl.id === controls);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-shop-look-trigger]');
    if (!trigger || !this.#triggerMatchesThisDrawer(trigger)) return;

    event.preventDefault();
    this.#openShopDrawer(/** @type {HTMLElement} */ (trigger));
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerEnter = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.pointerType === 'touch') return;

    const trigger = event.target.closest('[data-shop-look-trigger]');
    if (!trigger || !this.#triggerMatchesThisDrawer(trigger)) return;

    this.#prefetchLook(/** @type {HTMLElement} */ (trigger));
  };

  /**
   * @param {FocusEvent} event
   */
  #handleFocusIn = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-shop-look-trigger]');
    if (!trigger || !this.#triggerMatchesThisDrawer(trigger)) return;

    this.#prefetchLook(/** @type {HTMLElement} */ (trigger));
  };

  /**
   * @param {MouseEvent} event
   */
  #handleDrawerClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[data-shop-look-tab]');
    const drawerContent = this.#getDrawerContent();
    if (tab && drawerContent?.contains(tab)) {
      event.preventDefault();
      this.#activateTab(/** @type {HTMLButtonElement} */ (tab));
    }
  };

  /**
   * @param {HTMLElement} trigger
   */
  async #openShopDrawer(trigger) {
    const look = trigger.closest('[data-shop-look-look]');
    const dialog = this.#getDialogComponent();
    if (!dialog || !(look instanceof HTMLElement)) return;

    const dataEl = look.querySelector('[data-shop-look-panel-data]');
    if (dataEl) {
      await this.#openLazyDrawer(look, dataEl, dialog);
      return;
    }

    const template = look.querySelector('[data-shop-look-panel-template]');
    if (!(template instanceof HTMLTemplateElement)) {
      this.#setDrawerStatus('error');
      dialog.showDialog();
      return;
    }

    const panelRoot = template.content.querySelector('[data-shop-look-panel-root]');
    if (!panelRoot) {
      this.#setDrawerStatus('error');
      dialog.showDialog();
      return;
    }

    this.#activeLookId = null;
    this.#activePanelData = null;
    this.#setDrawerStatus('idle');
    this.#mountPanel(/** @type {HTMLElement} */ (panelRoot.cloneNode(true)));
    dialog.showDialog();
  }

  /**
   * @param {HTMLElement} look
   * @param {Element} dataEl
   * @param {import('./dialog').DialogComponent} dialog
   */
  async #openLazyDrawer(look, dataEl, dialog) {
    const panelData = this.#parsePanelData(dataEl);
    if (!panelData) {
      this.#setDrawerStatus('error');
      dialog.showDialog();
      return;
    }

    this.#openAbort?.abort();
    this.#openAbort = new AbortController();
    const { signal } = this.#openAbort;

    this.#activeLookId = panelData.panelId;
    this.#activePanelData = panelData;

    const primaryKey = panelData.inPhoto.length > 0 ? 'inPhoto' : 'similar';
    const cache = this.#ensureLookCache(panelData.panelId);

    dialog.showDialog();

    try {
      if (!this.#isTabLoaded(cache, primaryKey)) {
        this.#setDrawerStatus('loading');
        await this.#loadTabCards(panelData, primaryKey, cache);
      }

      if (signal.aborted) return;

      this.#setDrawerStatus('idle');
      this.#mountPanel(this.#buildLazyPanel(panelData, cache));
    } catch {
      if (signal.aborted) return;
      this.#setDrawerStatus('error');
    }
  }

  /**
   * @param {HTMLElement} trigger
   */
  #prefetchLook(trigger) {
    const look = trigger.closest('[data-shop-look-look]');
    if (!(look instanceof HTMLElement)) return;

    const dataEl = look.querySelector('[data-shop-look-panel-data]');
    if (!dataEl) return;

    const panelData = this.#parsePanelData(dataEl);
    if (!panelData) return;

    const primaryKey = panelData.inPhoto.length > 0 ? 'inPhoto' : 'similar';
    const cache = this.#ensureLookCache(panelData.panelId);
    if (this.#isTabLoaded(cache, primaryKey)) return;

    void this.#loadTabCards(panelData, primaryKey, cache).catch(() => {});
  }

  /**
   * @param {Element} dataEl
   * @returns {ShopLookPanelData | null}
   */
  #parsePanelData(dataEl) {
    try {
      const parsed = JSON.parse(dataEl.textContent ?? '');
      if (!parsed || typeof parsed !== 'object') return null;
      if (typeof parsed.panelId !== 'string') return null;

      return {
        panelId: parsed.panelId,
        labels: {
          tabs: String(parsed.labels?.tabs ?? ''),
          inPhoto: String(parsed.labels?.inPhoto ?? ''),
          similar: String(parsed.labels?.similar ?? ''),
          empty: String(parsed.labels?.empty ?? ''),
        },
        inPhoto: Array.isArray(parsed.inPhoto) ? parsed.inPhoto : [],
        similar: Array.isArray(parsed.similar) ? parsed.similar : [],
      };
    } catch {
      return null;
    }
  }

  /**
   * @param {string} lookId
   * @returns {ShopLookLookCache}
   */
  #ensureLookCache(lookId) {
    let cache = this.#lookCache.get(lookId);
    if (!cache) {
      cache = {
        inPhotoHtml: [],
        similarHtml: [],
        inPhotoLoaded: false,
        similarLoaded: false,
      };
      this.#lookCache.set(lookId, cache);
    }
    return cache;
  }

  /**
   * @param {ShopLookLookCache} cache
   * @param {'inPhoto' | 'similar'} key
   */
  #isTabLoaded(cache, key) {
    return key === 'inPhoto' ? cache.inPhotoLoaded : cache.similarLoaded;
  }

  /**
   * One in-flight load per look/tab so prefetch + open do not double-fetch.
   *
   * @param {ShopLookPanelData} panelData
   * @param {'inPhoto' | 'similar'} key
   * @param {ShopLookLookCache} cache
   */
  async #loadTabCards(panelData, key, cache) {
    if (this.#isTabLoaded(cache, key)) return;

    const inflightKey = `${panelData.panelId}:${key}`;
    let promise = this.#inflightLoads.get(inflightKey);

    if (!promise) {
      promise = (async () => {
        const refs = key === 'inPhoto' ? panelData.inPhoto : panelData.similar;
        const htmlItems = await this.#fetchCardHtmlList(refs);

        if (refs.length > 0 && htmlItems.length === 0) {
          throw new Error('Shop look cards failed to load');
        }

        if (key === 'inPhoto') {
          cache.inPhotoHtml = htmlItems;
          cache.inPhotoLoaded = true;
        } else {
          cache.similarHtml = htmlItems;
          cache.similarLoaded = true;
        }
      })().finally(() => {
        this.#inflightLoads.delete(inflightKey);
      });

      this.#inflightLoads.set(inflightKey, promise);
    }

    await promise;
  }

  /**
   * @param {ShopLookProductRef[]} refs
   * @returns {Promise<string[]>}
   */
  async #fetchCardHtmlList(refs) {
    if (!refs.length) return [];

    const results = await this.#mapPool(refs, FETCH_CONCURRENCY, (ref) => this.#fetchCardHtml(ref));
    return results.filter((html) => html !== '');
  }

  /**
   * @template T, R
   * @param {T[]} items
   * @param {number} concurrency
   * @param {(item: T, index: number) => Promise<R>} fn
   * @returns {Promise<R[]>}
   */
  async #mapPool(items, concurrency, fn) {
    /** @type {R[]} */
    const results = new Array(items.length);
    let nextIndex = 0;

    const worker = async () => {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;
        results[index] = await fn(items[index], index);
      }
    };

    const poolSize = Math.min(concurrency, items.length);
    await Promise.all(Array.from({ length: poolSize }, () => worker()));
    return results;
  }

  /**
   * @param {ShopLookProductRef} ref
   * @returns {Promise<string>} empty string when the card cannot be loaded
   */
  async #fetchCardHtml(ref) {
    try {
      if (!ref?.handle || !ref?.url) return '';

      const url = new URL(ref.url, window.location.origin);
      url.searchParams.set('section_id', CARD_SECTION_ID);

      const response = await fetch(url.toString());
      if (!response.ok) return '';

      const markup = await response.text();
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const item = doc.querySelector('[data-shop-look-card-render]');
      if (!(item instanceof HTMLElement)) return '';

      return item.outerHTML;
    } catch {
      return '';
    }
  }

  /**
   * @param {ShopLookPanelData} panelData
   * @param {ShopLookLookCache} cache
   * @returns {HTMLElement}
   */
  #buildLazyPanel(panelData, cache) {
    const inPhotoCount = panelData.inPhoto.length;
    const similarCount = panelData.similar.length;
    const panelId = panelData.panelId;
    const { labels } = panelData;

    const root = document.createElement('div');
    root.className = 'dev-shop-look-drawer-panel';
    root.setAttribute('data-shop-look-panel-root', '');
    root.setAttribute('data-in-photo-count', String(inPhotoCount));
    root.setAttribute('data-similar-count', String(similarCount));
    root.setAttribute('data-shop-look-lazy', '');

    if (inPhotoCount === 0 && similarCount === 0) {
      const empty = document.createElement('p');
      empty.className = 'dev-shop-look-drawer-panel__empty body_m';
      empty.textContent = labels.empty;
      root.append(empty);
      return root;
    }

    const tabInPhotoId = `${panelId}-tab-in-photo`;
    const tabSimilarId = `${panelId}-tab-similar`;
    const panelInPhotoId = `${panelId}-panel-in-photo`;
    const panelSimilarId = `${panelId}-panel-similar`;

    if (inPhotoCount > 0 && similarCount > 0) {
      const tablist = document.createElement('div');
      tablist.className = 'dev-shop-look-drawer-panel__tabs';
      tablist.setAttribute('role', 'tablist');
      tablist.setAttribute('aria-label', labels.tabs);
      tablist.setAttribute('data-shop-look-tabs', '');

      tablist.append(
        this.#createTabButton({
          id: tabInPhotoId,
          controls: panelInPhotoId,
          tabKey: 'in_photo',
          label: labels.inPhoto,
          selected: true,
        }),
        this.#createTabButton({
          id: tabSimilarId,
          controls: panelSimilarId,
          tabKey: 'similar',
          label: labels.similar,
          selected: false,
        })
      );
      root.append(tablist);
    }

    const scroll = document.createElement('div');
    scroll.className = 'dev-shop-look-drawer-panel__scroll';
    scroll.setAttribute('data-shop-look-panel-scroll', '');

    const isTabbed = inPhotoCount > 0 && similarCount > 0;

    if (inPhotoCount > 0) {
      scroll.append(
        this.#createTabPanel({
          id: panelInPhotoId,
          labelledBy: tabInPhotoId,
          panelKey: 'in_photo',
          heading: isTabbed ? null : labels.inPhoto,
          itemsHtml: cache.inPhotoLoaded ? cache.inPhotoHtml : [],
          hidden: false,
          loading: !cache.inPhotoLoaded,
          isTabbed,
        })
      );
    }

    if (similarCount > 0) {
      scroll.append(
        this.#createTabPanel({
          id: panelSimilarId,
          labelledBy: tabSimilarId,
          panelKey: 'similar',
          heading: isTabbed ? null : labels.similar,
          itemsHtml: cache.similarLoaded ? cache.similarHtml : [],
          hidden: inPhotoCount > 0,
          loading: !cache.similarLoaded,
          isTabbed,
        })
      );
    }

    root.append(scroll);
    return root;
  }

  /**
   * @param {{
   *   id: string,
   *   controls: string,
   *   tabKey: string,
   *   label: string,
   *   selected: boolean,
   * }} options
   */
  #createTabButton({ id, controls, tabKey, label, selected }) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'dev-shop-look-drawer-panel__tab h8';
    button.id = id;
    button.setAttribute('role', 'tab');
    button.setAttribute('aria-selected', selected ? 'true' : 'false');
    button.setAttribute('aria-controls', controls);
    button.setAttribute('data-shop-look-tab', tabKey);
    button.tabIndex = selected ? 0 : -1;

    const span = document.createElement('span');
    span.className = 'dev-shop-look-drawer-panel__tab-label';
    span.setAttribute('data-shop-look-tab-label', '');
    if (selected) span.setAttribute('data-dev-drawer-heading', '');
    span.textContent = label;
    button.append(span);
    return button;
  }

  /**
   * @param {{
   *   id: string,
   *   labelledBy: string,
   *   panelKey: string,
   *   heading: string | null,
   *   itemsHtml: string[],
   *   hidden: boolean,
   *   loading: boolean,
   *   isTabbed: boolean,
   * }} options
   */
  #createTabPanel({ id, labelledBy, panelKey, heading, itemsHtml, hidden, loading, isTabbed }) {
    const panel = document.createElement('div');
    panel.id = id;
    panel.className = 'dev-shop-look-drawer-panel__panel';
    if (hidden) {
      panel.classList.add('dev-shop-look-drawer-panel__panel--hidden');
      panel.hidden = true;
    }

    if (isTabbed) {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', labelledBy);
    } else {
      panel.setAttribute('role', 'region');
    }

    panel.setAttribute('data-shop-look-panel', panelKey);

    if (heading) {
      const headingId = `${id}-heading`;
      const h3 = document.createElement('h3');
      h3.id = headingId;
      h3.className = 'dev-shop-look-drawer-panel__single-heading h8';
      h3.setAttribute('data-shop-look-single-heading', '');
      h3.setAttribute('data-dev-drawer-heading', '');
      h3.textContent = heading;
      panel.setAttribute('aria-labelledby', headingId);
      panel.append(h3);
    }

    const list = document.createElement('ul');
    list.className = 'dev-shop-look-drawer-panel__grid list-unstyled wg-translate';
    list.setAttribute('role', 'list');
    list.setAttribute('data-shop-look-grid', panelKey);

    if (loading) {
      list.setAttribute('data-shop-look-grid-loading', '');
      list.setAttribute('aria-busy', 'true');
    } else {
      list.innerHTML = itemsHtml.join('');
    }

    panel.append(list);
    return panel;
  }

  /**
   * @param {HTMLElement} panel
   */
  #mountPanel(panel) {
    const drawerContent = this.#getDrawerContent();
    if (!drawerContent) return;

    drawerContent.replaceChildren(panel);
    this.#syncTabs(panel);
    this.#syncVisualHeading(panel);
    this.#retranslateDrawer();
  }

  /**
   * Weglot refresh after EN inject. Pass a grid for lazy fills (avoids wiping the other tab).
   * Full content (default): also re-sync compact titlebar from translated labels.
   * @param {HTMLElement} [scope]
   */
  #retranslateDrawer(scope) {
    const root = scope ?? this.#getDrawerContent();
    if (!(root instanceof HTMLElement)) return;

    const syncHeading = scope == null;

    requestAnimationFrame(() => {
      if (!root.isConnected) return;

      const chrome = /** @type {{ NoomaWeglotChrome?: { retranslate?: (node: HTMLElement) => void, prefixLinks?: (node: ParentNode) => void } }} */ (
        window
      ).NoomaWeglotChrome;
      chrome?.retranslate?.(root);
      chrome?.prefixLinks?.(root);

      if (!syncHeading) return;

      const panel = this.#getDrawerContent()?.querySelector('[data-shop-look-panel-root]');
      if (panel instanceof HTMLElement) {
        this.#syncVisualHeading(panel);
      }
    });
  }

  /**
   * Active tab label (or single-list heading) → compact titlebar + a11y heading.
   * @param {HTMLElement} panel
   */
  #syncVisualHeading(panel) {
    for (const el of panel.querySelectorAll('[data-dev-drawer-heading]')) {
      el.removeAttribute('data-dev-drawer-heading');
    }

    const activeTab = panel.querySelector('[data-shop-look-tab][aria-selected="true"]');
    const singleHeading = panel.querySelector(
      '[data-shop-look-panel]:not([hidden]) [data-shop-look-single-heading]'
    );
    const source =
      activeTab?.querySelector('[data-shop-look-tab-label]') ?? singleHeading;

    if (!(source instanceof HTMLElement)) return;

    source.setAttribute('data-dev-drawer-heading', '');
    const text = (source.textContent ?? '').trim().replace(/\s+/g, ' ');

    const titlebarLabel = this.querySelector('[data-shop-look-titlebar-label]');
    if (titlebarLabel instanceof HTMLElement) {
      titlebarLabel.textContent = text;
    }

    const a11yHeading = this.querySelector('[data-shop-look-a11y-heading]');
    if (a11yHeading instanceof HTMLElement) {
      a11yHeading.textContent = text;
    }

    const drawerRoot = this.#getDrawerRoot();
    if (drawerRoot && typeof window.__initDevVisualDrawer === 'function') {
      window.__initDevVisualDrawer(drawerRoot);
    }
  }

  /**
   * @param {HTMLElement} panel
   */
  #syncTabs(panel) {
    const tabs = panel.querySelectorAll('[data-shop-look-tab]');
    const tabPanels = panel.querySelectorAll('[data-shop-look-panel]');

    if (!tabs.length) return;

    for (const [index, tab] of Array.from(tabs).entries()) {
      const button = /** @type {HTMLButtonElement} */ (tab);
      const isFirst = index === 0;
      button.setAttribute('aria-selected', isFirst ? 'true' : 'false');
      button.tabIndex = isFirst ? 0 : -1;
    }

    for (const [index, tabPanel] of Array.from(tabPanels).entries()) {
      const isFirst = index === 0;
      if (isFirst) {
        tabPanel.removeAttribute('hidden');
        tabPanel.classList.remove('dev-shop-look-drawer-panel__panel--hidden');
      } else {
        tabPanel.setAttribute('hidden', '');
        tabPanel.classList.add('dev-shop-look-drawer-panel__panel--hidden');
      }
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleTabKeydown = (event) => {
    const tab = /** @type {HTMLButtonElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-shop-look-tab]') : null
    );

    const drawerContent = this.#getDrawerContent();
    if (!tab || !drawerContent?.contains(tab)) return;

    const tabs = Array.from(drawerContent.querySelectorAll('[data-shop-look-tab]'));
    const currentIndex = tabs.indexOf(tab);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#activateTab(tab);
      return;
    } else {
      return;
    }

    event.preventDefault();
    const nextTab = /** @type {HTMLButtonElement} */ (tabs[nextIndex]);
    nextTab.focus();
    this.#activateTab(nextTab);
  };

  /**
   * @param {HTMLButtonElement} tab
   */
  async #activateTab(tab) {
    const panelRoot = this.#getDrawerContent()?.querySelector('[data-shop-look-panel-root]');
    if (!panelRoot) return;

    const target = tab.dataset.shopLookTab;
    const tabs = panelRoot.querySelectorAll('[data-shop-look-tab]');
    const tabPanels = panelRoot.querySelectorAll('[data-shop-look-panel]');

    for (const item of tabs) {
      const button = /** @type {HTMLButtonElement} */ (item);
      const isActive = button.dataset.shopLookTab === target;
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.tabIndex = isActive ? 0 : -1;
    }

    for (const item of tabPanels) {
      const isActive = item instanceof HTMLElement && item.dataset.shopLookPanel === target;
      if (isActive) {
        item.removeAttribute('hidden');
        item.classList.remove('dev-shop-look-drawer-panel__panel--hidden');
      } else {
        item.setAttribute('hidden', '');
        item.classList.add('dev-shop-look-drawer-panel__panel--hidden');
      }
    }

    const body = this.#getDrawerBody();
    if (body instanceof HTMLElement) {
      body.scrollTop = 0;
    }

    const drawerRoot = this.#getDrawerRoot();
    drawerRoot?.classList.remove('dev-drawer--heading-scrolled');

    this.#syncVisualHeading(/** @type {HTMLElement} */ (panelRoot));

    if (
      panelRoot.hasAttribute('data-shop-look-lazy') &&
      this.#activePanelData &&
      this.#activeLookId &&
      (target === 'similar' || target === 'in_photo')
    ) {
      await this.#ensureLazyTabFilled(target === 'similar' ? 'similar' : 'inPhoto');
    }
  }

  /**
   * @param {'inPhoto' | 'similar'} key
   */
  async #ensureLazyTabFilled(key) {
    const panelData = this.#activePanelData;
    const lookId = this.#activeLookId;
    if (!panelData || !lookId) return;

    const cache = this.#ensureLookCache(lookId);
    if (this.#isTabLoaded(cache, key)) {
      this.#fillLazyGrid(key, cache);
      return;
    }

    const panelKey = key === 'inPhoto' ? 'in_photo' : 'similar';
    const grid = this.#getDrawerContent()?.querySelector(`[data-shop-look-grid="${panelKey}"]`);
    if (grid instanceof HTMLElement) {
      grid.setAttribute('data-shop-look-grid-loading', '');
      grid.setAttribute('aria-busy', 'true');
    }

    try {
      await this.#loadTabCards(panelData, key, cache);
      this.#fillLazyGrid(key, cache);
    } catch {
      this.#showLazyGridError(key);
    }
  }

  /**
   * @param {'inPhoto' | 'similar'} key
   * @param {ShopLookLookCache} cache
   */
  #fillLazyGrid(key, cache) {
    const panelKey = key === 'inPhoto' ? 'in_photo' : 'similar';
    const grid = this.#getDrawerContent()?.querySelector(`[data-shop-look-grid="${panelKey}"]`);
    if (!(grid instanceof HTMLElement)) return;

    const html = key === 'inPhoto' ? cache.inPhotoHtml : cache.similarHtml;
    grid.innerHTML = html.join('');
    grid.removeAttribute('data-shop-look-grid-loading');
    grid.removeAttribute('data-shop-look-grid-error');
    grid.removeAttribute('aria-busy');
    this.#retranslateDrawer(grid);
  }

  /**
   * Keep drawer chrome + other tab; show error only in the failed grid.
   * @param {'inPhoto' | 'similar'} key
   */
  #showLazyGridError(key) {
    const panelKey = key === 'inPhoto' ? 'in_photo' : 'similar';
    const grid = this.#getDrawerContent()?.querySelector(`[data-shop-look-grid="${panelKey}"]`);
    if (!(grid instanceof HTMLElement)) return;

    const errorText = this.#getDrawerStatus()?.dataset.errorText ?? '';
    grid.removeAttribute('data-shop-look-grid-loading');
    grid.setAttribute('data-shop-look-grid-error', '');
    grid.removeAttribute('aria-busy');
    grid.replaceChildren();

    const item = document.createElement('li');
    item.className = 'dev-shop-look-drawer-panel__grid-error body_m';
    item.setAttribute('data-shop-look-grid-error-text', '');
    item.textContent = errorText;
    grid.append(item);
    this.#retranslateDrawer(grid);
  }

  /**
   * @param {'loading' | 'error' | 'idle'} state
   */
  #setDrawerStatus(state) {
    const drawerStatus = this.#getDrawerStatus();
    if (!drawerStatus) return;

    const statusText = drawerStatus.querySelector('[data-shop-look-status-text]');
    const statusSpinner = drawerStatus.querySelector('[data-shop-look-status-spinner]');
    const drawerContent = this.#getDrawerContent();
    const drawerBody = this.#getDrawerBody();

    drawerStatus.dataset.state = state;

    if (drawerBody instanceof HTMLElement) {
      drawerBody.toggleAttribute('aria-busy', state === 'loading');
    }

    if (state === 'idle') {
      drawerStatus.hidden = true;
      if (statusSpinner instanceof HTMLElement) statusSpinner.hidden = true;
      return;
    }

    drawerStatus.hidden = false;
    drawerContent?.replaceChildren();

    if (statusSpinner instanceof HTMLElement) {
      statusSpinner.hidden = state !== 'loading';
    }

    if (!(statusText instanceof HTMLElement)) return;

    if (state === 'loading') {
      statusText.textContent = drawerStatus.dataset.loadingText ?? '';
      return;
    }

    statusText.textContent = drawerStatus.dataset.errorText ?? '';
  }
}

if (!customElements.get('dev-shop-look-drawer')) {
  customElements.define('dev-shop-look-drawer', DevShopLookDrawer);
}
